import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { logAccess } from '../../../lib/accessLogs';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
  email: string;
}

export default function EnvoyerSuiviPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');

  const [patient, setPatient] = useState<Patient | null>(null);
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  const [currentMedecinId, setCurrentMedecinId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { mutate } = useOfflineMutation<any>();

  // Formulaire
  const [medecinDestinataire, setMedecinDestinataire] = useState('');
  const [objet, setObjet] = useState('');
  const [message, setMessage] = useState('');
  const [diagnostique, setDiagnostique] = useState('');
  const [traitement, setTraitement] = useState('');
  const [notesMedicales, setNotesMedicales] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    loadData();
  }, [user, patientId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger le médecin actuel
      const { data: medecinData } = await supabase
        .from('medecins')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (medecinData) {
        setCurrentMedecinId(medecinData.id);

        // Charger la liste des médecins (sauf le médecin actuel)
        const { data: medecinsData } = await supabase
          .from('medecins')
          .select('id, nom, prenom, specialite, email')
          .neq('id', medecinData.id)
          .order('nom');

        if (medecinsData) {
          setMedecins(medecinsData);
        }
      }

      // Charger les informations du patient
      if (patientId) {
        const { data: patientData } = await supabase
          .from('patients')
          .select('id, nom, prenom')
          .eq('id', patientId)
          .single();

        if (patientData) {
          setPatient(patientData);
        }
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!objet.trim() || !message.trim() || !medecinDestinataire || !patientId || !currentMedecinId) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSending(true);
      const online = navigator.onLine;

      const suiviResult = await mutate({
        table: 'suivis_patients',
        operation: 'INSERT',
        data: {
          patient_id: patientId,
          medecin_expediteur_id: currentMedecinId,
          medecin_destinataire_id: medecinDestinataire,
          objet: objet.trim(),
          message: message.trim(),
          diagnostique: diagnostique.trim() || null,
          traitement: traitement.trim() || null,
          notes_medicales: notesMedicales.trim() || null,
          statut: 'envoye',
        },
        metadata: {
          summary: `Suivi — ${patient?.prenom} ${patient?.nom} → ${objet.trim()}`,
          performedBy: 'medecin',
        },
        onlineExecutor: async () => {
          const { data: suiviData, error } = await supabase
            .from('suivis_patients')
            .insert({
              patient_id: patientId,
              medecin_expediteur_id: currentMedecinId,
              medecin_destinataire_id: medecinDestinataire,
              objet: objet.trim(),
              message: message.trim(),
              diagnostique: diagnostique.trim() || null,
              traitement: traitement.trim() || null,
              notes_medicales: notesMedicales.trim() || null,
              statut: 'envoye',
            })
            .select()
            .single();

          if (error) throw error;
          return suiviData;
        },
      });

      // Log access (online only)
      if (online && user && suiviResult?.id) {
        await logAccess({
          userId: user.id,
          userRole: 'medecin',
          actionType: 'CREATE',
          resourceType: 'SUIVI',
          resourceId: suiviResult.id,
          patientId: patientId,
          details: {
            patient_nom: patient ? `${patient.prenom} ${patient.nom}` : 'Inconnu',
            objet: objet.trim(),
            medecin_destinataire_id: medecinDestinataire,
            action: 'Envoi d\'un suivi patient'
          }
        });
      }

      const msg = online
        ? 'Suivi envoyé avec succès !'
        : 'Suivi enregistré en mode hors-ligne. Synchronisation automatique au retour de la connexion.';
      alert(msg);
      navigate('/medecin/rendez-vous', { replace: true });
    } catch (error) {
      console.error('Erreur envoi suivi:', error);
      alert('Erreur lors de l\'envoi du suivi');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold text-gray-900">Patient non trouvé</p>
          <button
            onClick={() => navigate('/medecin/rendez-vous')}
            className="mt-6 px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark whitespace-nowrap cursor-pointer"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Envoyer un Suivi Patient</h1>
              <p className="mt-2 text-gray-600">
                Patient : {patient.prenom} {patient.nom}
              </p>
            </div>
            <button
              onClick={() => navigate('/medecin/rendez-vous')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Médecin destinataire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Médecin destinataire <span className="text-red-500">*</span>
            </label>
            <select
              value={medecinDestinataire}
              onChange={(e) => setMedecinDestinataire(e.target.value)}
              className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
              required
            >
              <option value="">Sélectionner un médecin</option>
              {medecins.map((medecin) => (
                <option key={medecin.id} value={medecin.id}>
                  Dr {medecin.prenom} {medecin.nom} - {medecin.specialite}
                </option>
              ))}
            </select>
          </div>

          {/* Objet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Objet du suivi <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={objet}
              onChange={(e) => setObjet(e.target.value)}
              placeholder="Ex: Suivi post-opératoire, Consultation de contrôle..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Décrivez la situation du patient, les raisons du suivi..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none"
              required
            />
          </div>

          {/* Diagnostic actuel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Diagnostic actuel <span className="text-gray-500">(facultatif)</span>
            </label>
            <textarea
              value={diagnostique}
              onChange={(e) => setDiagnostique(e.target.value)}
              rows={3}
              placeholder="Diagnostic établi lors de la dernière consultation..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none"
            />
          </div>

          {/* Traitement en cours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Traitement en cours <span className="text-gray-500">(facultatif)</span>
            </label>
            <textarea
              value={traitement}
              onChange={(e) => setTraitement(e.target.value)}
              rows={3}
              placeholder="Médicaments et traitements actuels du patient..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none"
            />
          </div>

          {/* Notes médicales */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes médicales <span className="text-gray-500">(facultatif)</span>
            </label>
            <textarea
              value={notesMedicales}
              onChange={(e) => setNotesMedicales(e.target.value)}
              rows={3}
              placeholder="Informations complémentaires, observations..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none"
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/medecin/rendez-vous')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={sending}
              className="px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer"
            >
              {sending ? 'Envoi en cours...' : 'Envoyer le suivi'}
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
}
